import type { ComplianceItem, ComplianceStatus, ComplianceType } from '@ip/shared-types';
import { t } from '@/i18n';
import { formatDateMedium } from '@/lib/hr';

/** Status → chip classes (light + dark), mirroring lib/hr STATUS_CHIP. */
export const COMPLIANCE_STATUS_CHIP: Record<ComplianceStatus, string> = {
  missing: 'border-zinc-400/40 bg-zinc-400/10 text-zinc-600 dark:text-zinc-400',
  pending_verification: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  valid: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  expired: 'border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-400',
  rejected: 'border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-400',
};

export function complianceTypeLabel(type: ComplianceType): string {
  return t(`hr.compliance.types.${type}`);
}

export function complianceStatusLabel(item: Pick<ComplianceItem, 'status' | 'validUntil'>): string {
  if (item.status === 'valid' && item.validUntil) {
    return t('hr.compliance.validUntil', { date: formatDateMedium(item.validUntil) });
  }
  return t(`hr.compliance.status.${item.status}`);
}

/** "zostało X dni" / "X dni po terminie" / "ostatni dzień ważności" — null when undated. */
export function complianceDaysLabel(item: Pick<ComplianceItem, 'daysLeft'>): string | null {
  if (item.daysLeft === null) return null;
  if (item.daysLeft === 0) return t('hr.compliance.dueToday');
  if (item.daysLeft < 0) return t('hr.compliance.daysOverdue', { days: -item.daysLeft });
  return t('hr.compliance.daysLeft', { days: item.daysLeft });
}

/** Urgency tint for the days-left figure (light + dark). */
export function complianceDaysTone(daysLeft: number | null): string {
  if (daysLeft === null) return 'text-muted-foreground';
  if (daysLeft < 0) return 'text-rose-600 dark:text-rose-400';
  if (daysLeft <= 7) return 'text-rose-600 dark:text-rose-400';
  if (daysLeft <= 30) return 'text-amber-600 dark:text-amber-400';
  return 'text-muted-foreground';
}
